import { APP_NAME } from '@trips/shared/config';
import { useEffect } from 'react';
import { selectSettings } from '../data/selectors';
import { useStore } from '../data/store';
import { onRefused } from '../data/sync';
import { PairScreen } from '../features/auth/PairScreen';
import { SetupScreen } from '../features/auth/SetupScreen';
import { SignInScreen } from '../features/auth/SignInScreen';
import { isNative } from '../lib/platform';
import { back, canGoBack, currentRoute, navigate, useRouter } from '../lib/router';
import { Toasts, toast } from '../ui/Toast';
import { Shell } from './Shell';

let splashHidden = false;

function hideSplash() {
  if (splashHidden) return;
  splashHidden = true;
  // Two frames: the first screen has been laid out and painted underneath.
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      void import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => SplashScreen.hide({ fadeOutDuration: 150 }))
        .catch(() => {});
    }),
  );
}

// Never leave the splash up if something goes wrong before the first screen.
if (isNative) setTimeout(hideSplash, 4000);

function useTheme() {
  const theme = useStore((s) => selectSettings(s).theme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') delete root.dataset.theme;
    else root.dataset.theme = theme;
    try {
      localStorage.setItem('trips.theme', theme);
    } catch {
      // Storage can be unavailable; the theme still applies for this visit.
    }
  }, [theme]);
}

function useAndroidBack() {
  useEffect(() => {
    if (!isNative) return;
    let remove: (() => void) | undefined;
    void import('@capacitor/app').then(({ App: CapApp }) => {
      const h = CapApp.addListener('backButton', () => {
        if (canGoBack()) back();
        else if (currentRoute().name !== 'trips')
          navigate('/', { replace: true, transition: 'pop' });
        else void CapApp.minimizeApp();
      });
      remove = () => void h.then((x) => x.remove());
    });
    return () => remove?.();
  }, []);
}

function useShortcuts() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === '/') {
        e.preventDefault();
        navigate('/search');
      } else if (e.key === 'n') {
        e.preventDefault();
        const r = currentRoute();
        navigate(r.params.tripId ? `/new?trip=${r.params.tripId}` : '/new', { overlay: true });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}

export function App() {
  const session = useStore((s) => s.session);
  const ready = useStore((s) => s.ready);
  const { route } = useRouter();
  useTheme();
  useAndroidBack();
  useShortcuts();

  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  // Android keeps its splash up until the first real screen has painted, then hides it once.
  const settled = ready && (!isNative || session !== 'unknown');
  useEffect(() => {
    if (isNative && settled) hideSplash();
  }, [settled]);

  useEffect(
    () => onRefused((reason) => toast(`A change couldn't be saved. ${reason}`, { tone: 'error' })),
    [],
  );

  let content: React.ReactNode;
  if (route.name === 'setup') content = <SetupScreen />;
  else if (session === 'needs-setup') content = <SetupScreen />;
  else if (session === 'needs-pairing') content = <PairScreen />;
  else if (session === 'signed-out') content = <SignInScreen />;
  else if (!ready) content = null;
  // On the phone, wait for the pairing check so the trips screen never flashes first.
  else if (isNative && session === 'unknown') content = null;
  else content = <Shell />;

  return (
    <>
      {content}
      <Toasts />
    </>
  );
}
